import React from 'react';
import { Order } from '../interfaces/Order';
import { groupProducts, getNormalizedProducts } from '../global/utils';

interface InvoiceReceiptProps {
    order: Order;
}

export const InvoiceReceipt = React.forwardRef<HTMLDivElement, InvoiceReceiptProps>(({ order }, ref) => {
    const priceToString = (num: any) => Number(num || 0).toLocaleString('es-CO');

    const formatDate = (dateValue: any) => {
        let date = new Date();
        if (dateValue) {
            if (dateValue.toDate) date = dateValue.toDate();
            else if (dateValue instanceof Date) date = dateValue;
            else date = new Date(dateValue);
        }
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; 
        const strTime = String(hours).padStart(2, '0') + ':' + minutes + ':' + seconds + ' ' + ampm;
        
        return `${day}/${month}/${year} ${strTime}`;
    };

    const normalizedProducts = getNormalizedProducts(order);
    const totalItems = normalizedProducts.reduce((acc, p) => acc + (p.quantity || 1), 0);
    const subtotal = order.subTotal || ((order.total ?? (order as any).totalPrice ?? 0) + (order.discountAmount || 0) - (order.tipAmount || 0));

    return (
        <div ref={ref} className="printable-invoice bg-white font-mono leading-tight" style={{ color: '#000', fontWeight: 'bold', fontFamily: '"Courier New", Courier, monospace', width: '280px', margin: '0 auto', fontSize: '12px', padding: '6px' }}>
            <style>{`
                @media print {
                    @page {
                        size: 80mm auto !important;
                        margin: 0mm !important;
                    }
                    html, body {
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                        width: 100% !important;
                        height: auto !important;
                    }
                    .printable-invoice {
                        width: 100% !important;
                        max-width: 80mm !important;
                        padding: 2mm 3mm !important;
                        margin: 0 !important;
                        box-shadow: none !important;
                    }
                }
            `}</style>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '12px', textAlign: 'center', fontWeight: '900' }}>
                <img src="/logoc.jpeg" alt="Thomas Pizza" style={{ width: '180px', height: 'auto', marginBottom: '8px', filter: 'grayscale(100%) brightness(0.8) contrast(1.2)' }} />
                <p style={{ margin: 0, fontSize: '16px', letterSpacing: '2px' }}>🍕 THOMAS PIZZA</p>
                <p style={{ margin: 0 }}>MEDELLIN - COLOMBIA</p>
                <p style={{ margin: 0 }}>GRACIAS POR VISITARNOS</p>
                <p style={{ margin: 0, marginTop: '4px' }}>**** CUENTA POR PAGAR ****</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ width: '100px' }}>Recibo:</span>
                    <span>{order.supportCode || (order.id ? order.id.slice(-6).toUpperCase() : "JS011850")}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ width: '100px' }}>Fecha:</span>
                    <span>{formatDate(order.dateSuccess || order.date)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ width: '100px' }}>Equipo:</span>
                    <span>DESKTOP-4DFJV64</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ width: '100px' }}>Atendido X:</span>
                    <span style={{ textTransform: 'uppercase' }}>{order.createdByName || "ADMIN"}</span>
                </div>
            </div>

            <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', borderBottom: '1px dashed black', paddingBottom: '4px', marginBottom: '4px' }}>
                    <span style={{ flex: 1 }}>Producto</span>
                    <span style={{ width: '50px', textAlign: 'right' }}>Precio</span>
                    <span style={{ width: '40px', textAlign: 'right' }}>Cant</span>
                    <span style={{ width: '60px', textAlign: 'right' }}>Valor</span>
                </div>
                {(!order.products || !Array.isArray(order.products)) ? (
                    ((order as any).pizzas || [order]).map((pizzaObj: any, pIndex: number) => {
                        const sName = pizzaObj.sizeName ? `Pizza ${pizzaObj.sizeName}` : "Pizza";
                        let addNames: any[] = [];
                        if (pizzaObj.additionNames) {
                            addNames = Array.isArray(pizzaObj.additionNames)
                                ? pizzaObj.additionNames
                                : Object.values(pizzaObj.additionNames);
                        }
                        if (addNames.length === 0 && (order as any).additionNames) {
                            addNames = Array.isArray((order as any).additionNames)
                                ? (order as any).additionNames
                                : Object.values((order as any).additionNames);
                        }
                        return (
                            <div key={pIndex} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px', lineHeight: '1.2' }}>
                                <span style={{ flex: 1, textTransform: 'uppercase', paddingRight: '4px', wordBreak: 'break-word' }}>
                                    {sName}
                                    {pizzaObj.slices && Array.isArray(pizzaObj.slices) && pizzaObj.slices.map((s: any, sIdx: number) => {
                                        let flavorLabel = s.flavorName || s.name || s.id || '';
                                        if (flavorLabel === 'build-your-own' || flavorLabel === 'Arma tu pizza' || flavorLabel === 'Personalizada') {
                                            flavorLabel = 'Personalizada';
                                        }
                                        const ingText = (s.ingredients && Array.isArray(s.ingredients) && s.ingredients.length > 0)
                                            ? `: ${s.ingredients.join(', ')}`
                                            : '';
                                        return (
                                            <div key={sIdx} style={{ fontSize: '0.85em', textTransform: 'none' }}>
                                                - {flavorLabel}{ingText}
                                            </div>
                                        );
                                    })}
                                    {addNames.map((add: any, aIdx: number) => (
                                        <div key={aIdx} style={{ fontSize: '0.85em', textTransform: 'none' }}>+ {typeof add === 'string' ? add : add?.name || 'Adición'}</div>
                                    ))}
                                </span>
                                <span style={{ width: '50px', textAlign: 'right' }}>{pIndex === 0 ? priceToString((order as any).totalPrice ?? order.total) : ''}</span>
                                <span style={{ width: '40px', textAlign: 'right' }}>x{pizzaObj.quantity ?? 1}</span>
                                <span style={{ width: '60px', textAlign: 'right' }}>{pIndex === 0 ? priceToString((order as any).totalPrice ?? order.total) : ''}</span>
                            </div>
                        );
                    })
                ) : (
                    groupProducts(order.products).map((p, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px', lineHeight: '1.2' }}>
                            <span style={{ flex: 1, textTransform: 'uppercase', paddingRight: '4px', wordBreak: 'break-word' }}>
                                {p.productName}
                                {p.variantLabel && <div style={{ fontSize: '0.85em', textTransform: 'none' }}>- {p.variantLabel}</div>}
                                {p.additions?.map((add, aIdx) => (
                                    <div key={aIdx} style={{ fontSize: '0.85em', textTransform: 'none' }}>+ {add.name} ({priceToString(add.price)})</div>
                                ))}
                                {p.discount && p.discount > 0 && (
                                    <div style={{ fontSize: '0.85em', textTransform: 'none', color: '#000' }}>
                                        DESC: -{p.discountMode === 'fixed' ? `$${priceToString(p.discount)}` : `${p.discount}%`}
                                    </div>
                                )}
                            </span>
                            <span style={{ width: '50px', textAlign: 'right' }}>{priceToString(p.price)}</span>
                            <span style={{ width: '40px', textAlign: 'right' }}>x{p.quantity}</span>
                            <span style={{ width: '60px', textAlign: 'right' }}>{priceToString(p.total ?? (p.price * p.quantity))}</span>
                        </div>
                    ))
                )}
                <div style={{ borderTop: '1px dashed black', marginTop: '4px' }}></div>
            </div>

            <div style={{ textTransform: 'uppercase', marginBottom: '16px', marginTop: '8px' }}>
                <p style={{ margin: 0 }}>PRODUCTOS A PAGAR EN CAJA:{totalItems}</p>
            </div>

            <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 'bold' }}>Subtotal:</span>
                    <span>{priceToString(subtotal)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Impuesto:</span>
                    <span>000</span>
                </div>
                
                {order.discountAmount ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Descuento:</span>
                        <span>-{priceToString(order.discountAmount)}</span>
                    </div>
                ) : null}

                {order.tipAmount ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Propina:</span>
                        <span>+{priceToString(order.tipAmount)}</span>
                    </div>
                ) : null}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '14px' }}>
                    <span style={{ fontWeight: 'bold' }}>TOTAL:</span>
                    <span style={{ fontWeight: 'bold' }}>{priceToString(order.total)}</span>
                </div>
                
                <p style={{ marginTop: '4px', fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase', marginBottom: '16px' }}>
                    {order.paymentMethod?.toLowerCase() === 'efectivo'
                        ? "VENTA EN EFECTIVO"
                        : order.paymentMethod?.toLowerCase() === 'transferencia'
                        ? "VENTA TRANSFERENCIA / NEQUI"
                        : order.paymentMethod?.toLowerCase() === 'tarjeta'
                        ? "VENTA CON TARJETA"
                        : `VENTA (${(order.paymentMethod || 'PAGO').toUpperCase()})`}
                </p>

                {order.paymentMethod?.toLowerCase() === 'tarjeta' && (
                    <p style={{ textAlign: 'center', marginBottom: '16px', margin: 0 }}>
                        Pago con tarjeta procesado
                    </p>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                    <span>MONTO</span>
                    <span>{priceToString(order.total)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '4px', fontSize: '13px' }}>
                    <span>Total</span>
                    <span>{priceToString(order.total)}</span>
                </div>
            </div>
        </div>
    );
});

InvoiceReceipt.displayName = 'InvoiceReceipt';
